import React, { useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Save, Plus, Pin, Check, X, Loader2 } from "lucide-react";
import {
  FORUM_PRESETS, FORUM_PRESET_ORDER, FORUM_QUICK_COLORS,
  themeFromSelection, buildForumPalette, buildForumBg, isValidHex,
} from "@/lib/forumThemes";

// Mini camo swatch for a preset tile — shows the preset's art VIVIDLY (lighter wash than the actual
// page background) so presets are easy to tell apart in the picker.
function PresetSwatch({ theme }) {
  return (
    <div className="relative h-[74px] w-full overflow-hidden border-2 border-black/40"
      style={{
        backgroundColor: "#0a0c06",
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.28)), url(${theme.topo}), url(${theme.camo})`,
        backgroundRepeat: "no-repeat, repeat, repeat",
        backgroundSize: "cover, 300px 300px, 180px 180px",
        filter: theme.patternFilter || "none",
      }}>
      <span className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 rounded-full border border-black/40" style={{ background: theme.accent }} />
    </div>
  );
}

// Live mini forum thread list — mirrors the real Forum page so a pick is WYSIWYG.
function MiniForumPreview({ theme }) {
  const P = useMemo(() => buildForumPalette(theme), [theme]);
  const bg = useMemo(() => buildForumBg(theme), [theme]);
  return (
    <div className="relative overflow-hidden border" style={{ borderColor: "#2a2b2d" }} data-testid="forum-theme-preview">
      <div aria-hidden className="absolute inset-0 z-0" style={bg} />
      <div className="relative z-[1] p-3.5">
        {/* header + accent button */}
        <div className="flex items-center justify-between gap-3">
          <span className="font-heading text-lg font-black uppercase tracking-tight" style={{ color: "#ffffff" }}>Forum</span>
          <span className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] font-bold tracking-wide px-2.5 py-1.5"
            style={{ background: P.accent, color: P.accentText }}>
            <Plus className="w-3 h-3" strokeWidth={2.5} /> New Thread
          </span>
        </div>
        {/* tabs — first active */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {["General", "Optics", "Hunting"].map((t, i) => (
            <span key={t} className="font-mono uppercase text-[9px] tracking-wide px-2 py-1 border"
              style={i === 0
                ? { background: P.chipActiveBg, color: P.chipActiveText, borderColor: P.chipActiveBg }
                : { background: P.chipBg, color: P.chipText, borderColor: P.chipBorder }}>
              {t}
            </span>
          ))}
        </div>
        {/* rows */}
        <div className="mt-3 flex flex-col gap-1.5">
          {[
            { pinned: true, title: "Welcome — read before posting", replies: 42 },
            { pinned: false, title: "Vortex Razor Gen III first impressions", replies: 137 },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 border" style={{ borderColor: P.cardBorder, background: P.rowSurface }}>
              <span className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-wide px-1.5 py-0.5 shrink-0"
                style={r.pinned
                  ? { color: P.goldText, background: P.gold }
                  : { color: P.tagLimeText, background: P.tagLimeBg, border: `1px solid ${P.tagLimeBorder}` }}>
                {r.pinned ? <Pin className="w-2.5 h-2.5" /> : null}{r.pinned ? "Pinned" : "General"}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] font-semibold" style={{ color: "#f3f3f0" }}>{r.title}</span>
              <span className="font-mono font-semibold text-base tabular-nums leading-none shrink-0" style={{ color: P.accent }}>{r.replies}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ForumPreferences({ open, onClose, me, onSaved }) {
  const initTheme = me?.forum_theme || "olive";
  const initHex = me?.forum_accent_hex || "#cfe86b";
  const [themeKey, setThemeKey] = useState(initTheme);
  const [accentHex, setAccentHex] = useState(isValidHex(initHex) ? initHex : "#cfe86b");
  const [busy, setBusy] = useState(false);

  const preview = useMemo(() => themeFromSelection(themeKey, accentHex), [themeKey, accentHex]);

  const initialCustomHex = initTheme === "custom" && isValidHex(initHex) ? initHex.toLowerCase() : null;
  const dirty = themeKey !== initTheme || (themeKey === "custom" && accentHex.toLowerCase() !== initialCustomHex);

  const pickPreset = (key) => setThemeKey(key);
  const pickCustom = (hex) => { setAccentHex(hex); setThemeKey("custom"); };

  const save = async () => {
    setBusy(true);
    try {
      const body = themeKey === "custom" ? { theme: "custom", accent_hex: accentHex } : { theme: themeKey };
      await api.put("/me/forum-theme", body);
      toast.success("Forum appearance saved.");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 bg-black/75 overflow-y-auto"
      data-testid="forum-preferences-panel" onClick={onClose}>
      <div className="w-full max-w-2xl border mt-2 sm:mt-6" style={{ background: "#0c0d0e", borderColor: "#2a2b2d" }} onClick={(e) => e.stopPropagation()}>
        {/* Head */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b" style={{ borderColor: "#2a2b2d" }}>
          <div>
            <h2 className="font-heading font-bold uppercase text-lg tracking-wide text-white">Forum Appearance</h2>
            <p className="font-mono text-[11.5px] mt-1 leading-relaxed" style={{ color: "#8B8D90" }}>
              Choose a color theme for the forum. Only visible to you — doesn't change what other members see.
            </p>
          </div>
          <button onClick={onClose} data-testid="forum-preferences-close" style={{ color: "#8B8D90" }}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Preset themes */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3.5">
            {FORUM_PRESET_ORDER.map((key) => {
              const t = FORUM_PRESETS[key];
              const active = themeKey === key;
              return (
                <button key={key} onClick={() => pickPreset(key)} data-testid={`forum-theme-preset-${key}`} aria-pressed={active}
                  className="text-left flex flex-col gap-2 group">
                  <div className={active ? "ring-2 ring-white ring-offset-0" : ""}>
                    <PresetSwatch theme={t} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {active && <Check className="w-3 h-3 shrink-0" style={{ color: "#fff" }} />}
                    <span className="font-mono text-[11.5px] font-semibold tracking-wide truncate" style={{ color: "#E8E8EA" }}>{t.label}</span>
                    {key === "olive" && (
                      <span className="font-mono text-[8.5px] tracking-wide px-1.5 py-0.5 font-semibold" style={{ background: "#cfe86b", color: "#0a0d05" }}>DEFAULT</span>
                    )}
                  </div>
                  <span className="font-mono text-[10px] -mt-1" style={{ color: "#6B6D70" }}>{t.sub}</span>
                </button>
              );
            })}
          </div>

          {/* Custom accent */}
          <div className="border-t pt-5" style={{ borderColor: "#2a2b2d" }}>
            <div className="font-mono text-[12px] tracking-wide" style={{ color: "#E8E8EA" }}>Or choose your own accent color</div>
            <div className="font-mono text-[10.5px] mt-1" style={{ color: "#6B6D70" }}>Background pattern re-tints to match. Overrides the preset above when set.</div>
            <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
              {FORUM_QUICK_COLORS.map((c) => {
                const active = themeKey === "custom" && accentHex.toLowerCase() === c.hex.toLowerCase();
                return (
                  <button key={c.name} onClick={() => pickCustom(c.hex)} data-testid={`forum-theme-quick-${c.name}`}
                    aria-label={c.name} aria-pressed={active} title={c.name}
                    className={`w-[26px] h-[26px] rounded-full border-2 transition-transform hover:scale-110 ${active ? "border-white" : "border-transparent"}`}
                    style={{ background: c.hex, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)" }}>
                    {active && <Check className="w-3.5 h-3.5 mx-auto" style={{ color: "#000" }} />}
                  </button>
                );
              })}
              <label className="relative w-[26px] h-[26px] rounded-full overflow-hidden cursor-pointer flex items-center justify-center border-2 border-transparent" title="Pick any color"
                style={{ background: "conic-gradient(red,yellow,lime,cyan,blue,magenta,red)", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)" }} data-testid="forum-theme-custom-label">
                <input type="color" value={accentHex} onChange={(e) => pickCustom(e.target.value)}
                  data-testid="forum-theme-custom-input" className="absolute inset-0 opacity-0 cursor-pointer" />
              </label>
              <span className="font-mono text-[12px] ml-1" style={{ color: "#8B8D90" }}>
                {themeKey === "custom" ? accentHex.toUpperCase() : "Preset"}
              </span>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] mb-2.5" style={{ color: "#6B6D70" }}>Live preview</p>
            <MiniForumPreview theme={preview} />
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end px-5 py-4 border-t" style={{ borderColor: "#2a2b2d" }}>
          <button onClick={save} disabled={busy || !dirty} data-testid="forum-theme-save"
            className="inline-flex items-center gap-2 disabled:opacity-50 font-mono font-semibold uppercase text-xs tracking-[0.06em] px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ background: "#57682A", color: "#f2f5e4" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {busy ? "Saving…" : "Save Preference"}
          </button>
        </div>
      </div>
    </div>
  );
}
